import React, { useEffect } from 'react';
import { Card, Grid } from '@material-ui/core';
import { FindInPage as FindInPageIcon } from '@material-ui/icons';

import CardTitle from '../../../common/card/CardTitle';
import CardContent from '../../../common/card/CardContent';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineRadioGroup, {
  InlineRadioGroupProps
} from '../../../common/inline/InlineRadioGroup';
import { WorkflowEntity } from '../../workflow/Workflow.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useUpdateSupplier } from '../Supplier.hooks';
import { BaseSupplierCardProps } from '../Supplier.interface';
import { StyledDivider, StyledTypography } from './SupplierCard.styled';

const SupplierQualityControlCard: React.FC<BaseSupplierCardProps> = (
  props: BaseSupplierCardProps
) => {
  const {
    supplier,
    supplierVersion,
    token,
    onChange,
    onUpdate,
    onError
  } = props;
  const {
    updatedSupplier,
    isUpdating,
    updateError,
    updateSupplier
  } = useUpdateSupplier(supplier.id, token);
  const { isFieldDisabled } = useTaskEntityFields<WorkflowEntity.Supplier>(
    supplier.task,
    { token }
  );
  const disabled = !!supplierVersion;
  const initialData = supplierVersion || supplier;
  const commonOptions: InlineRadioGroupProps['options'] = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
    { label: 'N/A', value: 'n/a' }
  ];
  const commonGridProps: InlineRadioGroupProps['grid'] = {
    xs: 12
  };

  useEffect(() => {
    if (updatedSupplier) {
      onChange?.(updatedSupplier);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedSupplier]);

  useEffect(() => {
    if (updateError) {
      onError?.(updateError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateError]);

  return (
    <Card>
      <CardTitle>
        <FindInPageIcon />
        Product Verification and Quality Control
      </CardTitle>
      <CardContent padding='large'>
        <InlineFormik
          isSubmitting={isUpdating}
          initialValues={{
            hasPassFailCriteria: initialData.hasPassFailCriteria || '',
            hasMeasurementVerification:
              initialData.hasMeasurementVerification || '',
            hasMeasurementResults: initialData.hasMeasurementResults || '',
            hasQualityEvidenceProgram:
              initialData.hasQualityEvidenceProgram || '',
            hasNonconformanceProgram: initialData.hasNonconformanceProgram || ''
          }}
          onSubmit={(supplierDto: Partial<any>) => {
            updateSupplier(supplierDto);
            onChange?.(supplierDto);
          }}
        >
          {() => (
            <InlineForm>
              <Grid item xs={12}>
                <StyledTypography variant='h5'>
                  Are measurement/verification requirements for product/service
                  acceptance documented and does it include:
                </StyledTypography>
              </Grid>
              <InlineRadioGroup
                name='hasPassFailCriteria'
                label='Criteria for acceptance/rejection'
                grid={commonGridProps}
                options={commonOptions}
                disabled={disabled || isFieldDisabled('hasPassFailCriteria')}
              />
              <InlineRadioGroup
                name='hasMeasurementVerification'
                label='Type of measurements/verification to be performed'
                grid={commonGridProps}
                options={commonOptions}
                disabled={
                  disabled || isFieldDisabled('hasMeasurementVerification')
                }
              />
              <InlineRadioGroup
                name='hasMeasurementResults'
                label='A record of measurement results'
                grid={commonGridProps}
                options={commonOptions}
                disabled={disabled || isFieldDisabled('hasMeasurementResults')}
              />
              <Grid item xs={12}>
                <StyledDivider />
              </Grid>
              <InlineRadioGroup
                name='hasQualityEvidenceProgram'
                label='Do records provide evidence that the product meets the defined requirements?'
                grid={commonGridProps}
                options={commonOptions}
                disabled={
                  disabled || isFieldDisabled('hasQualityEvidenceProgram')
                }
              />
              <InlineRadioGroup
                name='hasNonconformanceProgram'
                label='Is there a documented procedure in place for control and disposition of a non-conforming product?'
                grid={commonGridProps}
                options={commonOptions}
                disabled={
                  disabled || isFieldDisabled('hasNonconformanceProgram')
                }
              />
            </InlineForm>
          )}
        </InlineFormik>
      </CardContent>
    </Card>
  );
};

export default SupplierQualityControlCard;
